import { useState } from "react";
import { Check, Loader2, X } from "lucide-react";
import { Button, Input, Label, cn } from "@cliently/ui";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";

export type PromoCodeValidation = {
  valid: boolean;
  percentOff: number;
  source: "code" | "campaign" | null;
  reason?: string;
  amountBeforeCents: number;
  amountAfterCents: number;
  code: string;
};

type PromoCodeFieldProps = {
  orgId: string;
  serviceItemId?: string | null;
  amountCents: number;
  value: PromoCodeValidation | null;
  onChange: (value: PromoCodeValidation | null) => void;
  className?: string;
};



/**
 * Champ réutilisable permettant au client de saisir un code promo et de le valider
 * via l'endpoint public `POST /promotions/validate`. Retourne au parent le code
 * validé et la remise résolue, qui pourront être inclus dans l'appel de checkout.
 */
export const PromoCodeField = ({
  orgId,
  serviceItemId,
  amountCents,
  value,
  onChange,
  className,
}: PromoCodeFieldProps) => {
  const { t } = useTranslation();
  const [input, setInput] = useState<string>(value?.code ?? "");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const applied = value && value.valid;

  const apply = async () => {
    const trimmed = input.trim();
    const code = trimmed.toUpperCase();
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post<Omit<PromoCodeValidation, "code">>(
        "/promotions/validate",
        {
          orgId,
          code: code.length ? code : null,
          serviceItemId: serviceItemId ?? null,
          amountCents,
        },
      );
      if (data.valid) {
        onChange({ ...data, code: code.length ? code : "" });
      } else {
        setError(
          code.length
            ? t("promotions.codeField.invalid", "Invalid code")
            : t(
                "promotions.codeField.noActiveOffer",
                "No promotion applies to this amount",
              ),
        );
        onChange(null);
      }
    } catch {
      setError(
        code.length
          ? t("promotions.codeField.invalid", "Invalid code")
          : t(
              "promotions.codeField.noActiveOffer",
              "No promotion applies to this amount",
            ),
      );
      onChange(null);
    } finally {
      setLoading(false);
    }
  };

  const clear = () => {
    setInput("");
    setError(null);
    onChange(null);
  };

  return (
    <div className={cn("space-y-1.5", className)}>
      <Label>{t("promotions.codeField.label", "Promo code")}</Label>
      <div className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            if (applied) onChange(null);
            if (error) setError(null);
          }}
          placeholder={t(
            "promotions.codeField.placeholder",
            "Enter a code",
          )}
          className="font-mono uppercase"
          disabled={loading}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              apply();
            }
          }}
        />
        {applied ? (
          <Button type="button" variant="outline" onClick={clear}>
            <X className="mr-1 h-4 w-4" />
            {t("common.remove", "Remove")}
          </Button>
        ) : (
          <Button
            type="button"
            onClick={apply}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : null}
            {t("promotions.codeField.apply", "Apply")}
          </Button>
        )}
      </div>
      {applied && (
        <p className="text-primary flex items-center gap-1 text-xs font-medium">
          <Check className="h-3 w-3" />
          {t("promotions.codeField.applied", {
            percent: value!.percentOff,
            defaultValue: "-{{percent}}% applied",
          })}
        </p>
      )}
      {error && (
        <p className="text-destructive text-xs font-medium">{error}</p>
      )}
    </div>
  );
}
